import { createFileRoute } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { useMemo } from 'react';
import { Grid3x3, Eye, ShieldCheck, AlertTriangle, HelpCircle } from 'lucide-react';
import { useTenantCoverage } from '../hooks/useTenantCoverage';
import { AttackCoverageMatrix } from '../components/range/AttackCoverageMatrix';
import type { CoverageCell, CoverageSummary } from '../api/types';
import { Badge, PageContainer, PageHeader, Section } from '../components/ui';
import { cardItem, staggerContainer } from '../lib/motion';

/**
 * ATT&CK navigator — tenant-wide MITRE ATT&CK coverage, clean professional SaaS.
 * A PageHeader, a coverage headline (score + verdict breakdown), and the
 * reusable AttackCoverageMatrix in a clean Section. The bas service
 * (`getTenantCoverage`) rolls every range's per-technique rows into one
 * best-verdict cell per technique; this view answers "which techniques can our
 * detections catch across the fleet, and where are the gaps?".
 */

export const Route = createFileRoute('/attack-navigator')({
  component: AttackNavigator,
});

function toNum(v: string | number | undefined): number {
  if (typeof v === 'number') return v;
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/** Headline figures from the server summary, falling back to the cell list. */
function headline(summary: CoverageSummary | undefined, cells: ReadonlyArray<CoverageCell>) {
  if (summary && summary.total !== undefined) {
    return {
      total: toNum(summary.total),
      prevented: toNum(summary.prevented),
      detected: toNum(summary.detected),
      missed: toNum(summary.missed),
      errored: toNum(summary.errored),
      score: toNum(summary.coverage_score),
    };
  }
  const c = { total: cells.length, prevented: 0, detected: 0, missed: 0, errored: 0 };
  for (const cell of cells) {
    const rec = c as Record<string, number>;
    if (cell.status in c) rec[cell.status] = (rec[cell.status] ?? 0) + 1;
  }
  const scored = c.total - c.errored;
  return { ...c, score: scored ? (c.prevented + c.detected) / scored : 0 };
}

function AttackNavigator() {
  const { data, isPending, isError } = useTenantCoverage();

  const cells = useMemo<ReadonlyArray<CoverageCell>>(() => data?.matrix ?? [], [data]);
  const counts = useMemo(() => headline(data?.summary, cells), [data, cells]);
  const pct = Math.round(counts.score * 100);
  const tactics = useMemo(
    () => new Set(cells.map((c) => (c.tactic && c.tactic.trim()) || 'uncategorized')).size,
    [cells],
  );

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={Grid3x3}
        title="ATT&CK coverage"
        description="Tenant-wide MITRE ATT&CK coverage — the best detection verdict per technique across the whole fleet."
        breadcrumbs={[{ label: 'Operate' }, { label: 'ATT&CK coverage' }]}
        actions={<Badge variant="success">Tenant navigator</Badge>}
      />

      {/* Coverage headline */}
      <Section title="Coverage summary" description="How much of the ATT&CK surface your detections can catch.">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 gap-3 lg:grid-cols-12"
        >
          {/* Coverage % hero */}
          <motion.div variants={cardItem} whileHover={{ y: -2 }} className="hud-panel p-5 lg:col-span-4">
            <div className="flex items-start justify-between">
              <span className="eyebrow">Fleet coverage</span>
              <Eye className="size-4 text-neon" />
            </div>
            <div className="mt-3 flex items-baseline gap-3">
              <span className="readout__value text-5xl text-neon">{isPending ? '—' : `${pct}%`}</span>
              <span className="text-[12px] text-txt-muted">
                {counts.total} technique{counts.total === 1 ? '' : 's'}
              </span>
            </div>
            <div className="mt-4 flex items-center justify-between border-t border-bd pt-3 text-[12px] text-txt-muted">
              <span className="tabular">
                {tactics} tactic{tactics === 1 ? '' : 's'}
              </span>
              <span className="tabular">
                {data?.rangeCount ?? 0} range{(data?.rangeCount ?? 0) === 1 ? '' : 's'}
              </span>
            </div>
          </motion.div>

          {/* Verdict breakdown */}
          <motion.div variants={cardItem} whileHover={{ y: -2 }} className="hud-panel p-5 lg:col-span-8">
            <div className="mb-4 flex items-center gap-2">
              <Grid3x3 className="size-4 text-neon" />
              <span className="text-[14px] font-semibold text-txt-bright">Coverage verdicts</span>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <LegendStat
                Icon={ShieldCheck}
                tone="text-neon"
                label="Prevented"
                value={counts.prevented}
                hint="blocked before execution"
              />
              <LegendStat
                Icon={Eye}
                tone="text-hud-cyan"
                label="Detected"
                value={counts.detected}
                hint="executed, alert fired"
              />
              <LegendStat
                Icon={AlertTriangle}
                tone="text-hud-red"
                label="Missed"
                value={counts.missed}
                hint="executed, no alert — the gap"
              />
              <LegendStat
                Icon={HelpCircle}
                tone="text-txt-2"
                label="Errored"
                value={counts.errored}
                hint="harness could not fire"
              />
            </div>
          </motion.div>
        </motion.div>
      </Section>

      {/* ATT&CK matrix */}
      <Section
        title="Technique matrix"
        description="Best verdict per technique, grouped by ATT&CK tactic across all ranges."
      >
        <div className="hud-panel p-4">
          {isPending ? (
            <div className="py-10 text-center text-[12.5px] text-txt-muted">Loading tenant coverage…</div>
          ) : isError ? (
            <div className="py-10 text-center text-[12.5px] text-hud-red">Could not load tenant coverage.</div>
          ) : (
            <AttackCoverageMatrix
              cells={cells}
              summary={data?.summary}
              caption="Tenant-wide · best verdict per technique across all ranges"
            />
          )}
        </div>
      </Section>
    </PageContainer>
  );
}

function LegendStat({
  Icon,
  tone,
  label,
  value,
  hint,
}: {
  Icon: typeof Eye;
  tone: string;
  label: string;
  value: number;
  hint: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5">
        <Icon className={`size-4 ${tone}`} />
        <span className={`tabular text-[18px] font-bold ${tone}`}>{value}</span>
        <span className="text-[13px] text-txt-2">{label}</span>
      </div>
      <span className="text-[11px] text-txt-muted">{hint}</span>
    </div>
  );
}
